import type { DeviceID } from 'src/types/DeviceID';
import { DOOR_STATE, INPUTS_READERS, MOTION_DETECTION, SENSOR_DETECTION } from 'src/constants';
import {
	VENTORA_FAN_SCENE_UI,
	ventoraFanOnUiToCloud,
} from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';

import { _init_exchange_based_conditions } from 'src/components/Scenes/helpers/init_exchange_based_conditions';
import { SCENE_WEATHER_VALUE_TO_UI } from 'src/components/Scenes/helpers/utilities_scenes_to_ui';
import { isVCType } from './virtual_components/virtual_component_utils';
import {
	buildCameraSceneToken,
	CAMERA_COND_NAMES,
	convertCameraUiValueToSceneValue,
	isCameraSceneCommand,
	isCameraUiCondName,
} from '../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';

/** --- DEVICE HELPERS ----- */
/**
 * Converts UI property name to Scene Service command format
 * @param name - UI property name (e.g., 'turn', 'brightness', 'temperature')
 * @param ch - Channel number
 * @param mode - Optional device mode
 * @returns Scene Service command string (e.g., 'r1', 'brightness2', 't1')
 */
export const convertUiPropertyToSceneCommand = (name, ch, mode?) => {
	let channel = parseInt(ch) + 1;
	// 2000 is the starting channel number for Blu TRV devices
	// BluTRV
	if (channel > 2000) {
		switch (name) {
			case 'valve_percent_position':
				return `pos${channel}`;

			case 'valve_open_state':
				return `isopen${channel}`;

			case 'target_temp':
				return `tgt${channel}`;

			case 'temperature':
				return `tmp${channel}`;

			case 'battery':
				return `bat${channel}`;

			case 'tmp':
				return `tmp${channel}`;

			case 'trv_target_temperature':
				return `tgt${channel}`;

			case 'boost':
				return `boost${channel}`;

			default:
				console.log('could not match condition for blu trv ', name);
				return name;
		}
	} else if (channel > 200 && isVCType(name)) {
		//Virtual component
		return name + '' + channel;
	}
	// We separate presence, since we do not have a hardcoded value for it
	// S4SN-0U61X presence zone conditions

	if (
		name === 'presence_zone_state' ||
		name === 'presence_zone_objects' ||
		name === 'presence_zone_objects_delta' ||
		name === 'presence' ||
		name.startsWith('prsn_')
	) {
		return name; // General presence detection
	}

	const cameraSceneToken = buildCameraSceneToken(name, ch);
	if (cameraSceneToken) {
		return cameraSceneToken;
	}

	if (name === CAMERA_COND_NAMES.ZONE_MOTION) {
		return name;
	}

	switch (name) {
		case 'turn':
		case 'turn_add_on':
			return `r${channel}`;
		case 'state':
			return 'state'; // check for sute that it does not includes roller

		case 'meter':
		case 'power meter':
			return `m${channel}`;

		case 'roller state':
			return 'rl';

		case 'position':
		case 'roller position':
			return 'rlp';

		case 'roller direction':
			return 'rld';

		case 'multi_cover_state':
			return `rl${channel}`;

		case 'multi_cover_position':
			return `rlp${channel}`;

		case 'blinds_tilt_position':
			return 'rbp';

		case 'multi_cover_blinds_tilt_position':
			return `rbp${channel}`;

		case 'brightness':
			if (mode === 'color') return 'gain';
			return `brightness${channel}`;

		case 'ct':
			return `ct${channel}`;

		case 'vol_meter':
		case 'vol_meter_add_on':
			return `v${channel}`;

		case 'tariff':
			return `m${channel}tariff`;

		case 'input_value':
		case 'digital_input_add_on':
			return `i${channel}`;

		case 'push_type':
		case 'push_type_add_on':
			// Remote control push: encode input index: ie1/ie2/... ("c" added later if channel is selected)
			return `ie${channel}`;

		case 'analog_input_add_on':
			return `ip${channel}`;

		// These checks by mode are unmaintainable! Needs refactor ASAP!
		case 'temperature':
			if (
				// Addon peripheral channels are always above 100 and appear in scene conditions as `t${channel}`
				channel > 100 ||
				mode === 'relay' ||
				mode === 'inputs_reader' ||
				mode === 'roller' ||
				mode === 'light' ||
				mode === 'rgbw' ||
				mode === 'rgb' ||
				mode === 'white'
			)
				return `t${channel}`;
			if (mode === 'thermostat') return `tmp${channel}`;
			return 'tmp';

		case 'humidity':
			if (
				// Addon peripheral channels are always above 100 and appear in scene conditions as `t${channel}`
				channel > 100 ||
				mode === 'relay' ||
				mode === 'inputs_reader' ||
				mode === 'roller' ||
				mode === 'light' ||
				mode === 'rgbw' ||
				mode === 'rgb' ||
				mode === 'white'
			)
				return `h${channel}`;
			return 'hum';

		case 'moisture':
			return `moisture${channel}`;

		case 'illumination':
			return 'lux_i';

		case 'luxury':
			return 'lux';

		case 'battery':
			return 'bat';

		case 'distance':
			return 'dist';

		case 'vibration':
			return 'vibr';

		case 'white_channel':
			return 'white';

		case 'adc':
			return `adc${channel}`;

		case 'ext_switch':
			return `es${channel}`;

		case 'total_current':
			return 'mt';

		case 'valve_percent_position':
			return `pos${channel}`;

		case 'valve_open_state':
			//when open
			return `isopen${channel}`;

		case 'target_temp':
			return `tgt${channel}`;

		case 'set_profile':
			return `sch${channel}`;

		case 'report_window_open':
			return `wo${channel}`;

		case 'boost':
			return `boost${channel}`;

		case 'reported_by':
			return `rep`;

		// Zendure statuses
		case 'solar_input_power':
			return 'solarInputPower';

		case 'output_home_power':
			return 'outputHomePower';

		case 'electricLevel':
			return 'electricLevel';

		// Zendure action
		case 'outputLimit':
		case 'battery_output_limit':
			return 'outputLimit';
		case 'bolt_state':
			return 'bolt_state';
		case 'remote_control_channel':
			return `channel`;
		case 'scroll_position':
			// Remote control scroll: base trigger is always rotie1 ("c" added later if channel is selected)
			return `rotie1`;
		case 'scroll_steps':
			// SBRC: non-negative step count with gt/lt; pair with rotie1 (scroll_position) for direction when needed
			return 'rotie1s';

		//Cury diffuser
		case 'intensity_left':
			return 'cil';
		case 'intensity_right':
			return 'cir';
		case 'level_left':
			return 'cll';
		case 'level_right':
			return 'clr';
		// case 'ambient_enable':This scene isn't triggering yet
		//     return 'cae';
		case 'presence':
			return 'presence'; // General presence detection

		case VENTORA_FAN_SCENE_UI.ON:
			return 'on';
		case VENTORA_FAN_SCENE_UI.SPEED:
			return 'speed';
		case VENTORA_FAN_SCENE_UI.SOURCE:
			return 'source';

		default:
			console.log('Utilities UI to Scenes: name: ', name);
			return name;
	}
};

/**
 * Converts UI values to Scene Service value format
 * @param trigger - Scene Service trigger/command
 * @param value - UI value to convert
 * @returns Converted value in Scene Service format
 */
export const convertUiValueToSceneValue = (trigger, value) => {
	const triggerValue = trigger.startsWith('isopen')
		? 'isopen'
		: trigger.startsWith('m') && trigger.endsWith('tariff')
			? 'tariff'
			: trigger;
	// this is for the case when the trigger is isopen1, or  isopen2202, 2201 and what ever number comes after "isopen"
	// "isopen1" comes from trv g1, "isopen"+nember comes from bluTRV
	console.warn('triggerValue', trigger, value);
	switch (triggerValue) {
		case 'door':
		case 'window':
			return DOOR_STATE.find((el) => value === el.ui).check || 'on';
		case 'motion':
		case 'vibr':
		case 'pir':
			return MOTION_DETECTION.find((el) => el.ui === value).check || 'on';
		case 'ie1':
		case 'ie2':
		case 'ie3':
		case 'ie4':
		case 'ie5':
		case 'ie6':
		case 'ie7':
		case 'ie8':
			return INPUTS_READERS.find((el) => el.ui === value).check || 'S';
		case 'flood':
		case 'smoke':
		case 'gas':
		case 'moisture_alarm':
			return SENSOR_DETECTION.find((el) => el.ui === value).check || 'false';

		case 'ppm':
			return value.toString();

		case 'isopen':
			//back-end  expects 'on' or 'off' for isopen
			return value === 'open' ? 'on' : 'off';

		case 'tariff':
			return Number(value.replace('T', ''));

		case 'channel':
			//This is for remote control channel
			// Handle optional channel selection
			if (value === 'any') {
				return 'any'; // Special value for any channel
			}
			return Number(value.split('_')[1]);
		case 'remote_control_conditions':
			// Handle the condition group conversion
			return {
				push_type: value?.push_type || 'short',
				remote_control_channel: value?.remote_control_channel || undefined,
				scroll_position: value?.scroll_position || 'any',
			};

		case 'scroll_position':
			return value === 'scroll_up' ? 'L' : value === 'scroll_down' ? 'R' : value;
		case 'rotation0':
			return Number(value);

		case 'on':
			return ventoraFanOnUiToCloud(value);

		default:
			if (isCameraSceneCommand(trigger) || isCameraUiCondName(trigger)) {
				return convertCameraUiValueToSceneValue(String(value));
			}
			// Handle ie1c format for remote control push with channel
			if (trigger.match(/ie[0-9]c/)) {
				return `S${trigger.match(/ie([0-9])c/)[1]}`;
			}
			// Handle rotie1c format for remote control scroll with channel
			if (trigger.match(/rotie[0-9]c/)) {
				return `R${trigger.match(/rotie([0-9])c/)[1]}`; // R plus channel number
			}
			if (trigger.match(/ie[0-9]{3}/)) {
				return INPUTS_READERS.find((el) => el.ui === value).check || 'S';
			}
			return value;
	}
};

/** --- END DEVICE HELPERS ----- */

/** --- WEATHER HELPERS ------ */
/**
 * Scene Service definition
 * 	let c:conditions_t={
        tst:from.dt,
        t:from.main.temp,
        tn:from.main.temp_min,
        tx:from.main.temp_max,
        h:from.main.humidity,
        //if you change this please also change update_conditions(...) below 
        p:from.main.pressure,
        w:from.wind.speed,
        ww:windforce(from.wind.speed),
        d:from.wind.deg,
        dd:deg2dd(from.wind.deg),
        wg:from.wind.gust,
        wwg:windforce(from.wind.gust),
        fl:from.main.feels_like,
        //if you change this please also change update_conditions(...) below 
        pop:(from.pop!=undefined?from.pop:unknown_pop),
        c:from.clouds.all,
        cc:clouds2name(from.clouds.all),
        tc:0,
        dc:0,
        //if you change this please also change update_conditions(...) below 
        rc:0,
        sc:0,
        ac:0,
    }
 */
/** for ww in scene service
 * if (speed<1) return 'wf0';
    if (speed<=5) return 'wf1';
    if (speed<=11) return 'wf2';
    if (speed<=19) return 'wf3';
    if (speed<=28) return 'wf4';
    if (speed<=38) return 'wf5';
    if (speed<=49) return 'wf6';
    if (speed<=61) return 'wf7';
    if (speed<=74) return 'wf8';
    if (speed<=88) return 'wf9';
    if (speed<=102) return 'wf10';
    if (speed<=117) return 'wf11';

    return 'wf12'

    based on this https://www.rmets.org/metmatters/beaufort-wind-scale
 */

/**
 *  * for clouds - cc
 * if (clouds<=10) return 'clear';
    if (clouds<=25) return 'few';
    if (clouds<=50) return 'scattered';
    if (clouds<=84) return 'broken';
    return 'overcast'
    based on 
    800	Clear	clear sky	
    801	Clouds	few clouds: 11-25%
    802	Clouds	scattered clouds: 25-50%
    803	Clouds	broken clouds: 51-84%
    804	Clouds	overcast clouds: 85-100%	
    https://openweathermap.org/weather-conditions

 */

/**
 * * New cloud percentages by research
 *
 * Clear sky: 0-10%
 * Partly Cloudy: 11-40%
 * Moslty Cloudy: 41-70%
 * Cloudy: 71-90%
 * Overcast: 91-100%
 * https://rapidapi.com/sangatpuria01/api/weather-com
 * https://www.datamensional.com/
 * https://openweathermap.org/api
 * https://www.weatherbit.io/api
 */
const UI_WEATHER_TO_SCENE_MAPPING = {
	// snow: 'sc',
	// rain: 'rc',
	clouds: 'cc',
	wind_speed: 'w',
	wind_force: 'wf',
	wind_direction: 'dd',
	temperature: 't',
	humidity: 'h',
	chance_precipitation: 'pop',
};

const WEATHER_API_CODES_BY_STR = {
	//for wind force and wind gust force
	calm: 'wf0', //<1km/h
	light_air: 'wf1', //1-5km/h
	light_breeze: 'wf2', //6-11km/h
	gentle_breeze: 'wf3', //12-19km/h
	moderate_breeze: 'wf4', //20-28km/h
	fresh_breeze: 'wf5', //29-38km/h
	strong_breeze: 'wf6', //38-49km/h
	near_gale: 'wf7', //50-61km/h
	gale: 'wf8', //62-74km/h
	strong_gale: 'wf9', //75-88km/h
	storm: 'wf10', //89-102km/h
	violent_storm: 'wf11', //103-117 km/h
	hurricane: 'wf12',

	//for clouds
	clear: 'clear_sky', // 0%-10%
	few: 'partly_cloudy', // 11-25%
	scattered: 'mostly_cloudy', // 26-50%
	broken: 'cloudy', // 51-84%
	overcast: 'overcast', // 85-100%//don't change key names, they are used in the API.

	//new types of clouds
	// clear_sky: 'clear', //0%-10%
	// partly_cloudy: 'partly', //11-40%
	// mostly_cloudy: 'mostly', //41-70%
	// cloudy: 'cloudy', //71-90%
	// overcast: 'overcast', //91%-100%

	//for wind direction
	north: 'N',
	north_northeast: 'NNE',
	northeast: 'NE',
	east_northeast: 'ENE',
	east: 'E',
	east_southeast: 'ESE',
	southeast: 'SE',
	south_southeast: 'SSE',
	south: 'S',
	south_southwest: 'SSW',
	southwest: 'SW',
	west_southwest: 'WSW',
	west: 'W',
	west_northwest: 'WNW',
	northwest: 'NW',
	north_northwest: 'NNW',

	// 'light-snow': 600,
	// snow: 601,
	// 'heavy-snow': 602,
	// 'light-rain-and-snow': 615,
	// 'rain-and-snow': 616,

	// 'light-rain': 500,
	// 'moderate-rain': 501,
	// 'heavy-intensity-rain': 502,
	// 'very-heavy-rain': 503
};

/**
 * Converts UI weather condition to Scene Service weather condition format
 * @param cond - Weather condition name (e.g., 'clouds', 'temperature')
 * @param op - Operator ('equal', 'less_than', 'greater_than')
 * @param val - Condition value
 * @returns Object with condition, value, and operator in Scene Service format
 */
export const convertUiWeatherToSceneCondition = (cond, op, val) => {
	let condition,
		value,
		operator = 'eq';
	if (op !== 'equal') {
		operator = op === 'less_than' ? 'lt' : 'gt';
	}

	switch (cond) {
		case 'clouds':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['clouds'],
				value: Object.keys(SCENE_WEATHER_VALUE_TO_UI)[
					Object.values(SCENE_WEATHER_VALUE_TO_UI).indexOf(val)
				], //for cloud conditions using the values from translations to match the api values
				operator: 'eq',
			};
		case 'wind_direction':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['wind_direction'],
				value: WEATHER_API_CODES_BY_STR[val.replace(/-/g, '_')],
				operator: 'eq',
			};

		case 'wind_force':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['wind_force'],
				value: WEATHER_API_CODES_BY_STR[val.replace(/\s/g, '_')],
				operator,
			};
		case 'chance_precipitation':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['chance_precipitation'],
				value: Number(val),
				operator,
			};
		// case 'wind_gust':
		// 	/**
		// 	- wind.speed Wind speed. Unit Default: meter/sec, Metric: meter/sec, Imperial: miles/hour.
		// 	- wind.deg Wind direction, degrees (meteorological)
		// 	- wind.gust Wind gust. Unit Default: meter/sec, Metric: meter/sec, Imperial: miles/hour
		// 	 */
		// 	return { condition, value, operator };
		// case 'rain':
		// case 'snow':
		// 	console.log('cond, value', cond, val);
		// 	return {
		// 		condition: UI_WEATHER_TO_SCENE_MAPPING[cond],
		// 		value: WEATHER_API_CODES_BY_STR[val.replace(/\s/g, '-')],
		// 		operator: 'eq'
		// 	};

		case 'temperature':
		case 'humidity':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING[cond],
				value: Number(val),
				operator,
			};
		// case 'uv':
		default:
			return { condition, value, operator };
	}
};

/** ---- END WEATHER HELPERS ----- */

export function exchangeMarketCountryAndRegionNames(exchangeMarket) {
	const components = _init_exchange_based_conditions();

	let energy_exchange_component = components.find((el) => el.cond === 'energy_exchange');

	let countries = energy_exchange_component.countries;

	const exchangeMarketArr = exchangeMarket.split('|');
	const countryName = exchangeMarketArr[0];
	let regionName = '';
	if (exchangeMarketArr.length > 0) {
		regionName = exchangeMarketArr[1];
	}

	let selectedCountry = countries?.find((el) => el.value === countryName);

	let selectedRegion = selectedCountry?.regions.find((reg) => {
		return reg.value === regionName;
	});

	return {
		selectedCountry,
		selectedRegion,
		countries,
		energy_exchange_component,
	};
}

/**
 * Create a device_id string from a deviceID object {id: string, channel: number}
 *  Used for _gui_device_id - override the interfeace device id for GUI purposes in the scenes
 * @param deviceID {DeviceID} - Object with id and channel
 * @returns {string} - Device id string in the format of `<device id>_<channel>`
 */
export function createGuiDeviceId(deviceID: DeviceID) {
	if (Number(deviceID.channel) > 0) {
		return `${deviceID.id}_${deviceID.channel}`;
	}
	return `${deviceID.id}`;
}
